import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({ baseURL: BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cb_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing = null;

const tryRefreshToken = async () => {
  if (refreshing) return refreshing;
  const oldToken = localStorage.getItem('cb_token');
  if (!oldToken) return null;
  refreshing = axios
    .post(`${BASE_URL}/tenant/refresh-token`, { token: oldToken }, { timeout: 10000 })
    .then((res) => {
      const newToken = res.data?.data?.token;
      if (newToken) localStorage.setItem('cb_token', newToken);
      return newToken || null;
    })
    .catch(() => null)
    .finally(() => { refreshing = null; });
  return refreshing;
};

const redirectToLogin = () => {
  localStorage.removeItem('cb_token');
  if (window.location.pathname !== '/app/login') window.location.href = '/app/login';
};

const redirectToPlanos = () => {
  if (!window.location.pathname.startsWith('/app/planos')) window.location.href = '/app/planos';
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;
    const original = error.config;
    const code = error.response?.data?.code;

    if (status === 401 && !original?._retried) {
      original._retried = true;
      // Não tentar refresh em endpoints de auth
      const isAuthRoute = original.url?.includes('/tenant/login')
        || original.url?.includes('/tenant/register')
        || original.url?.includes('/tenant/refresh-token');
      if (!isAuthRoute && code !== 'INVALID_CREDENTIALS') {
        const newToken = await tryRefreshToken();
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      }
      redirectToLogin();
      return Promise.reject(error);
    }
    if (status === 402 && (code === 'TRIAL_EXPIRED' || code === 'TENANT_SUSPENDED')) {
      toast.error(message || 'Assine um plano para continuar usando a Ruvily.', { icon: '🔒', id: 'blocked' });
      redirectToPlanos();
    }
    else if (status === 402) toast.error(message || 'Limite do plano atingido.', { icon: '🔒' });
    else if (status === 429) toast.error('Muitas requisições. Aguarde um momento.');
    else if (status >= 500) toast.error('Erro interno. Tente novamente.');
    return Promise.reject(error);
  }
);

export const authService = {
  login: (email, password) => api.post('/tenant/login', { email, password }),
  register: (data) => api.post('/tenant/register', data),
  verifyEmail: (token) => api.post('/tenant/verify-email', { token }),
  resendVerification: (email) => api.post('/tenant/resend-verification', { email }),
  forgotPassword: (email) => api.post('/tenant/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/tenant/reset-password', { token, password }),
  refreshToken: (token) => api.post('/tenant/refresh-token', { token }),
  getMe: () => api.get('/tenant/me'),
};

export const botService = {
  list: () => api.get('/tenant/bots'),
  create: (data) => api.post('/tenant/bots', data),
  getById: (id) => api.get(`/tenant/bots/${id}`),
  update: (id, data) => api.patch(`/tenant/bots/${id}`, data),
  delete: (id) => api.delete(`/tenant/bots/${id}`),
};

export const billingService = {
  listPlans: () => api.get('/billing/plans'),
  getSubscription: () => api.get('/billing/subscription'),
  subscribe: (planId, cpfCnpj) =>
    api.post('/billing/subscribe', cpfCnpj ? { planId, cpfCnpj } : { planId }),
  cancel: () => api.post('/billing/cancel'),
  listInvoices: () => api.get('/billing/invoices'),
  getUsage: () => api.get('/billing/usage'),
  getUsageSeries: (period) => api.get('/billing/usage/series', { params: { period } }),
};

export const chatService = {
  sendMessage: (botId, sessionId, message) => api.post('/chat/message', { botId, sessionId, message }),
  getHistory: (sessionId, botId) => api.get(`/chat/history/${sessionId}`, { params: { botId } }),
};

// URL que o cliente cadastra como webhook no app da Meta: raiz do backend
// (sem o sufixo /api) + /webhooks/whatsapp
export const metaWebhookUrl = () => {
  const base = BASE_URL.startsWith('http')
    ? BASE_URL
    : window.location.origin + BASE_URL;
  return base.replace(/\/api\/?$/, '') + '/webhooks/whatsapp';
};

export const whatsappService = {
  connect: (botId) => api.post(`/tenant/bots/${botId}/whatsapp/connect`),
  connectMeta: (botId, data) =>
    api.post(`/tenant/bots/${botId}/whatsapp/meta`, data),
  getQR: (botId) => api.get(`/tenant/bots/${botId}/whatsapp/qr`),
  getStatus: (botId) => api.get(`/tenant/bots/${botId}/whatsapp/status`),
  disconnect: (botId) => api.post(`/tenant/bots/${botId}/whatsapp/disconnect`),
};

export const aiSettingsService = {
  get: () => api.get('/tenant/settings/ai'),
  save: (data) => api.post('/tenant/settings/ai', data),
  removeKey: () => api.delete('/tenant/settings/ai/key'),
};

export const conversationService = {
  list: (botId) => api.get(`/tenant/bots/${botId}/conversations`),
  getMessages: (conversationId) => api.get(`/tenant/conversations/${conversationId}/messages`),
  takeOver: (conversationId) => api.post(`/tenant/conversations/${conversationId}/takeover`),
  release: (conversationId) => api.post(`/tenant/conversations/${conversationId}/release`),
  sendMessage: (conversationId, message) => api.post(`/tenant/conversations/${conversationId}/send`, { message }),
};

export const operatorService = {
  savePhone: (phone) => api.post('/tenant/settings/operator-phone', { phone }),
};

export const searchService = {
  query: (q) => api.get('/search', { params: { q } }),
};

export const calendarService = {
  getConnection: () => api.get('/calendar/connection'),
  startConnection: () => api.post('/calendar/connection/start'),
  disconnect: () => api.delete('/calendar/connection'),
  listProfessionals: () => api.get('/calendar/professionals'),
  listEvents: (params) => api.get('/calendar/events', { params }),
  createEvent: (data) => api.post('/calendar/events', data),
  deleteEvent: (eventId, professionalId) =>
    api.delete(`/calendar/events/${eventId}`, { params: professionalId ? { professionalId } : {} }),
};

export const userService = {
  list: () => api.get('/tenant/users'),
  invite: (data) => api.post('/tenant/users/invite', data),
  update: (userId, data) => api.patch(`/tenant/users/${userId}`, data),
  remove: (userId) => api.delete(`/tenant/users/${userId}`),
  regenerateInvite: (userId) => api.post(`/tenant/users/${userId}/invite-link`),
  inviteInfo: (token) => api.get(`/tenant/invite/${token}`),
  acceptInvite: (token, password) =>
    api.post(`/tenant/invite/${token}/accept`, { password }),
};

export const anamneseService = {
  listQuestions: () => api.get('/anamnese/questions'),
  createQuestion: (data) => api.post('/anamnese/questions', data),
  updateQuestion: (id, data) => api.patch(`/anamnese/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/anamnese/questions/${id}`),
  reorderQuestions: (order) => api.post('/anamnese/questions/reorder', { order }),
  send: (conversationId) => api.post('/anamnese/send', { conversationId }),
  listResponses: () => api.get('/anamnese/responses'),
  getResponse: (id) => api.get(`/anamnese/responses/${id}`),
  responsesByPhone: (phone) => api.get(`/anamnese/responses/by-phone/${encodeURIComponent(phone)}`),
  getPublicForm: (token) => api.get(`/public/anamnese/${token}`),
  submitPublicForm: (token, answers) =>
    api.post(`/public/anamnese/${token}/submit`, { answers }),
};

export default api;
